import React from "react";
import { Helmet } from "react-helmet-async";
import {
  BRAND,
  ROUTE_META,
  ROBOTS,
  canonicalFor,
  buildWebPageJsonLd,
} from "./entity";

/**
 * SEOHead — reusable route-level SEO surface.
 *
 * Renders:
 *   - <title>
 *   - <meta name="description">
 *   - <meta name="robots">
 *   - <link rel="canonical">  (only for indexable routes with a canonical
 *       destination; omitted on noindex or parameterized pages to avoid
 *       creating a misleading canonical)
 *   - Open Graph tags (og:title, og:description, og:url, og:type, og:image,
 *       og:site_name)
 *   - Twitter card tags (summary_large_image + title + description)
 *   - WebPage JSON-LD wired via @id to the static Organization + WebSite
 *       entities in index.html (no duplication)
 *
 * Metadata sources (in priority order):
 *   1. Explicit props (`title`, `description`, ...) — used verbatim.
 *   2. `path` prop → looks up ROUTE_META[path].
 *   3. Falls back to brand defaults.
 *
 * Notes:
 * - `indexable=false` suppresses BOTH the canonical link and the WebPage
 *   JSON-LD, because those pages should not enter the search index.
 * - `path` should be the app-relative pathname (e.g. "/check"), NOT the
 *   full URL. Canonical is built via SITE_ORIGIN so preview and prod agree.
 */
export function SEOHead({
  path,
  title,
  description,
  ogTitle,
  ogDescription,
  ogImage,
  robots,
  indexable,
  canonicalPath,
}) {
  const meta = (path && ROUTE_META[path]) || {};

  const resolvedTitle = title || meta.title || BRAND.name;
  const resolvedDescription =
    description || meta.description || BRAND.tagline;
  const resolvedOgTitle = ogTitle || meta.ogTitle || resolvedTitle;
  const resolvedOgDescription =
    ogDescription || meta.ogDescription || resolvedDescription;
  const resolvedOgImage = ogImage || meta.ogImage || BRAND.defaultOgImage;
  const resolvedRobots = robots || meta.robots || ROBOTS.INDEX;
  const resolvedIndexable =
    typeof indexable === "boolean"
      ? indexable
      : typeof meta.indexable === "boolean"
      ? meta.indexable
      : resolvedRobots.startsWith("index");

  const canonicalTargetPath = canonicalPath || path || null;
  const canonical =
    resolvedIndexable && canonicalTargetPath
      ? canonicalFor(canonicalTargetPath)
      : null;

  const webPageJsonLd =
    resolvedIndexable && canonical
      ? buildWebPageJsonLd({
          canonical,
          title: resolvedTitle,
          description: resolvedDescription,
        })
      : null;

  return (
    <Helmet prioritizeSeoTags>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="robots" content={resolvedRobots} />
      {canonical ? <link rel="canonical" href={canonical} /> : null}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={BRAND.name} />
      <meta property="og:title" content={resolvedOgTitle} />
      <meta property="og:description" content={resolvedOgDescription} />
      {canonical ? <meta property="og:url" content={canonical} /> : null}
      <meta property="og:image" content={resolvedOgImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedOgTitle} />
      <meta name="twitter:description" content={resolvedOgDescription} />
      <meta name="twitter:image" content={resolvedOgImage} />

      {webPageJsonLd ? (
        <script type="application/ld+json">
          {JSON.stringify(webPageJsonLd)}
        </script>
      ) : null}
    </Helmet>
  );
}

export default SEOHead;
